import React, { useState } from "react";
import { Send, Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import RevealOnScroll from "@/components/RevealOnScroll";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOUR_OPTIONS = [
  "Golden Triangle Classic",
  "Rajasthan Heritage Grand Tour",
  "Spiritual India (Varanasi & Rishikesh)",
  "Wildlife Tiger Safari",
  "Kashmir Paradise",
  "Honeymoon Romance Tour",
  "Nepal Extension",
  "Dubai Extension",
  "Thailand Extension",
  "Custom Itinerary",
];

export default function QuickEnquirySection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tour_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, travelers: 2 });
      toast.success(data.message || "Inquiry received! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", tour_interest: "", message: "" });
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 md:py-32 bg-cream relative overflow-hidden" data-testid="home-quick-enquiry">
      <div className="absolute inset-0 mandala-bg pointer-events-none opacity-40" />
      <div className="container-tti relative grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <RevealOnScroll>
            <div className="eyebrow mb-4">Quick Enquiry</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mb-6">
              Get a free itinerary
              <br />
              <span className="italic text-deepred">in under a minute.</span>
            </h2>
            <p className="text-foreground/75 font-light leading-relaxed mb-8 max-w-md">
              Tell us where you dream of going. Our travel curators reply with a tailor-made plan
              and pricing within 24 hours — no obligation, no spam.
            </p>
            <a
              href="tel:+917017798524"
              className="inline-flex items-center gap-3 text-sm font-medium text-deepred hover:text-saffron transition-colors"
              data-testid="quick-enquiry-call"
            >
              <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-5 h-5" />
              </div>
              Prefer to talk? Call +91 70177 98524
            </a>
          </RevealOnScroll>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white border border-cream-200 rounded-sm p-8 md:p-10 shadow-sm">
            <form onSubmit={submit} className="space-y-5" data-testid="quick-enquiry-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="qe-name">Full Name *</Label>
                  <Input
                    id="qe-name"
                    required
                    data-testid="quick-enquiry-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="bg-cream"
                  />
                </div>
                <div>
                  <Label htmlFor="qe-email">Email *</Label>
                  <Input
                    id="qe-email"
                    type="email"
                    required
                    data-testid="quick-enquiry-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com"
                    className="bg-cream"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="qe-phone">Phone (with country code) *</Label>
                  <Input
                    id="qe-phone"
                    required
                    data-testid="quick-enquiry-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+44 7700 900123"
                    className="bg-cream"
                  />
                </div>
                <div>
                  <Label>Tour Interest</Label>
                  <Select value={form.tour_interest} onValueChange={update("tour_interest")}>
                    <SelectTrigger className="bg-cream" data-testid="quick-enquiry-tour">
                      <SelectValue placeholder="Choose a tour" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOUR_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="qe-message">Tell us about your dream trip</Label>
                <Textarea
                  id="qe-message"
                  data-testid="quick-enquiry-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Duration, interests, budget, number of travelers..."
                  rows={3}
                  className="bg-cream"
                />
              </div>

              <button
                type="submit"
                data-testid="quick-enquiry-submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <>Send Enquiry <Send className="w-4 h-4 ml-2" /></>
                )}
              </button>
              <p className="text-xs text-center text-muted-foreground">
                We respect your privacy. Your details are only used to respond to this enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
